import {
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Add } from '@mui/icons-material';
import useTable from 'src/hooks/useTable';
import { TableHeadCustom } from 'src/components/table';
import { useEpisodeOfCares, usePractitionerRoles } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { TABLE_HEAD_EPISODE_OF_CARE } from 'src/careflow/common/table-head';
import { v4 as uuidv4 } from 'uuid';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { translateTableHead } from 'src/careflow/helpers/common';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { Row } from './Row';
import { EpisodeOfCareWrapper, WrappedEpisodeOfCare } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';
import { EpisodeOfCareForm } from './EpisodeOfCareForm';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import EpisodeOfCareDetailDrawer from './EpisodeOfCareDetailDrawer';

type Props = {
  patient: WrappedPatient | null;
};

export function EpisodeOfCareList({ patient }: Props) {
  const [add, setOpenAdd] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedEpisodeOfCare, setSelectedEpisodeOfCare] = useState<WrappedEpisodeOfCare | null>(null);
  const [episodeOfCareToEdit, setEpisodeOfCareToEdit] = useState<WrappedEpisodeOfCare | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});

  const { i18n } = useLocales();

  const [episodesOfCare, { refresh: refetchEpisodesOfCare }] =
    useEpisodeOfCares({
      filter: { patient: patient?.id },
      map: EpisodeOfCareWrapper,
      autofetch: !!patient?.id,
    });

  const [practitionerRoles,] = usePractitionerRoles({
    filter: { active: true },
    map: PractitionerRoleWrapper,
  });

  const paginatedEpisodesOfCare =
    episodesOfCare?.slice?.(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <TableContainer>
            <Stack sx={{ m: 1 }}>
              <Grid container display={'flex'} alignItems={'center'} paddingRight={2}>
                {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.EPISODE_OF_CARE.ADD] }) && (
                  <Grid item xs={2} display={'flex'} justifyContent={'flex-end'}>
                    <Button
                      variant='contained'
                      size="small"
                      sx={{ height: '36px' }}
                      startIcon={<Add />}
                      onClick={() => setOpenAdd(true)}
                      style={{ position: 'absolute', top: '25px', right: '25px' }}
                    >
                      {i18n('patients.details.episodeOfCare.titleAddEpisodeOfCare', 'crs')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Stack>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHeadCustom headLabel={translateTableHead(TABLE_HEAD_EPISODE_OF_CARE, i18n, 'crs')} />
              <TableBody>
                {paginatedEpisodesOfCare.length ? (
                  paginatedEpisodesOfCare.map((row: any) => (
                    <Row
                      key={uuidv4()}
                      row={row}
                      patient={patient}
                      practitionerRoles={practitionerRoles}
                      onRowClick={(episodeOfCare) => {
                        setSelectedEpisodeOfCare(episodeOfCare);
                        setIsDrawerOpen(true);
                      }}
                      onDetailsClick={(episodeOfCare) => {
                        setSelectedEpisodeOfCare(episodeOfCare);
                        setIsDrawerOpen(true);
                      }}
                    />
                  ))
                ) : (
                  <TableCell colSpan={TABLE_HEAD_EPISODE_OF_CARE?.length}>
                    <Typography variant="body2" align="center">
                      No rows
                    </Typography>
                  </TableCell>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={episodesOfCare?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>
      <EpisodeOfCareForm
        practitionerRoles={practitionerRoles}
        patient={patient}
        episodeOfCare={null}
        open={add}
        onClose={() => setOpenAdd(false)}
      />
      <EpisodeOfCareDetailDrawer
        episodeOfCare={selectedEpisodeOfCare}
        open={isDrawerOpen}
        onCloseIconButtonClick={() => {
          setIsDrawerOpen(false);
          setSelectedEpisodeOfCare(null);
        }}
        onEdit={(episodeOfCare) => {
          setIsDrawerOpen(false);
          setEpisodeOfCareToEdit(episodeOfCare);
          setIsEditModalOpen(true);
        }}
        onSuccesfulEdit={() => {
          refetchEpisodesOfCare();
        }}
        isAllowedToEdit={checkAclValidation({ acls: [crsAcls.CRS.PATIENT.EPISODE_OF_CARE.EDIT] })}
        title="Episode Of Care Details"
      />
      <EpisodeOfCareForm
        practitionerRoles={practitionerRoles}
        patient={patient}
        episodeOfCare={episodeOfCareToEdit}
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEpisodeOfCareToEdit(null);
        }}
      />
    </>
  );
}
